import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

interface RelatedPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
}

export function PublicRelatedPosts({ siteId, postId, siteSlug, platformHost }: { siteId: string; postId: string; siteSlug: string; platformHost: boolean }) {
  const { data: posts = [] } = useQuery({
    queryKey: ['public-related-posts', siteId, postId],
    queryFn: async (): Promise<RelatedPost[]> => {
      const { data: categories, error: categoriesError } = await supabase
        .from('post_terms')
        .select('term_id')
        .eq('post_id', postId);
      if (categoriesError) throw categoriesError;

      const termIds = (categories ?? []).map((item) => item.term_id);
      if (termIds.length === 0) return [];

      const nowIso = new Date().toISOString();
      const { data, error } = await supabase
        .from('posts')
        .select('id, title, slug, excerpt, post_terms!inner(term_id)')
        .eq('site_id', siteId)
        .neq('id', postId)
        .not('type_id', 'is', null)
        .or(`status.eq.published,and(status.eq.scheduled,scheduled_at.lte.${nowIso})`)
        .in('post_terms.term_id', termIds)
        .order('published_at', { ascending: false })
        .limit(3);
      if (error) throw error;
      return (data ?? []) as RelatedPost[];
    },
    enabled: Boolean(siteId && postId),
  });

  const uniquePosts = posts.filter((post, index) => posts.findIndex((item) => item.id === post.id) === index);
  if (uniquePosts.length === 0) return null;

  return (
    <section className="w-full border-t border-slate-200 py-14 md:py-20">
      <div className="mx-auto max-w-6xl px-6">
        <h2 className="font-display text-2xl font-bold" style={{ color: 'var(--color-heading, #0f172a)' }}>Leia também</h2>
        <div className="mt-6 grid gap-4 md:grid-cols-3">
          {uniquePosts.map((post) => (
            <a
              key={post.id}
              href={platformHost ? `/s/${siteSlug}/${post.slug}` : `/${post.slug}`}
              className="block border border-slate-200 bg-white p-5 transition-colors hover:border-slate-300"
            >
              <h3 className="font-semibold leading-snug" style={{ color: 'var(--color-heading, #0f172a)' }}>{post.title}</h3>
              {post.excerpt && <p className="mt-3 line-clamp-3 text-sm leading-relaxed" style={{ color: 'var(--color-paragraph, #475569)' }}>{post.excerpt}</p>}
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}
